import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button, EmptyState, LoadingBlock, Notice } from '@wodi/ui';

import { staysQuery, visitStayBillingQuery } from '../api/inpatient-billing.ts';
import styles from '../billing/billing.module.css';
import { money } from '../billing/format.ts';
import { errorText } from '../stock/common.tsx';
import { StayBillingSection } from './stay-billing.tsx';
import { balanceWords } from './words.ts';

// The cashier's view of inpatient stays (docs/plans/phase2-inpatient-billing.md §6): the
// admitted and recently discharged patients with their deposit and balance, and, when the
// open bill is a stay's, the stay card above the visit bill.

/** The stay card when the chosen visit is an inpatient stay; nothing for any other visit. */
export function CashierStayCard({ visitId }: { visitId: string }) {
  const stay = useQuery({ ...visitStayBillingQuery(visitId), refetchInterval: 15_000 });
  if (stay.isPending) return <LoadingBlock label="Checking for an inpatient stay" rows={1} />;
  if (stay.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void stay.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(stay.error, "The inpatient stay couldn't be loaded. Try again.")}
      </Notice>
    );
  if (stay.data.stay === null) return null;
  return <StayBillingSection billing={stay.data.stay} place="cashier" />;
}

/** Admitted and recently discharged patients, for deposits and final bills. */
export function InpatientStays({ selected }: { selected: string | null }) {
  const stays = useQuery({ ...staysQuery, refetchInterval: 30_000 });
  return (
    <div>
      <div className={styles.queueHeader}>
        <h2 className={styles.sectionHeading}>Inpatients</h2>
        {stays.isSuccess && stays.data.items.length > 0 ? (
          <span className={styles.meta}>
            {stays.data.items.length === 1 ? '1 stay' : `${String(stays.data.items.length)} stays`}
          </span>
        ) : null}
      </div>
      {stays.isPending ? <LoadingBlock label="Loading inpatients" rows={2} /> : null}
      {stays.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void stays.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(stays.error, "Inpatients couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {stays.isSuccess && stays.data.items.length === 0 ? (
        <EmptyState>No one is admitted here now.</EmptyState>
      ) : null}
      {stays.isSuccess && stays.data.items.length > 0 ? (
        <nav aria-label="Inpatient stays" className={styles.queue}>
          {stays.data.items.map((s) => {
            const words = balanceWords({
              balance: s.balance,
              balanceState:
                BigInt(s.balance.cents) > 0n
                  ? 'owes'
                  : BigInt(s.balance.cents) < 0n
                    ? 'refund_due'
                    : 'settled',
            });
            return (
              <Link
                key={s.admissionId}
                to="/cashier"
                search={{ visitId: s.visitId }}
                className={styles.queueItem}
                {...(selected === s.visitId ? { 'aria-current': 'page' as const } : {})}
              >
                <span className={styles.queueLine}>
                  <strong>{s.patient.name}</strong>
                  <span className="wodi-num">{`${words.label} ${words.amount}`}</span>
                </span>
                <span className={`${styles.queueLine} ${styles.meta}`}>
                  <span className="wodi-id">{s.visitNumber}</span>
                  <span>
                    {s.status === 'admitted'
                      ? `Admitted, deposit ${money(s.depositHeld)}`
                      : s.finalBilled
                        ? 'Discharged, final bill made'
                        : 'Discharged, final bill to make'}
                  </span>
                </span>
              </Link>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
}
